// 로그인 카드(비밀번호 단계)의 체크 둘 — 「아이디 저장」 · 「로그인 유지(7일)」.
//
// 왜 여기 있나: 2단계 카드(TotpCards)를 무는 서비스 다섯이 같은 로그인 카드에 같은 체크 둘을 붙인다
// (사람 지시 2026-10-10, b3x 에픽 t-mops-login-remember). 서비스마다 만들면 문구·순서·저장 키가 갈린다.
// 이 패키지를 이미 다섯이 물고 있어 새 link: 를 늘리지 않으려고 totp-ui 에 뒀다 — 로그인 전 화면 조각이다.
//
// 뜻(서버가 지킨다 — 이 조각은 값만 넘긴다):
// - 아이디 저장: 브라우저 localStorage 에 **아이디만** 둔다. 비밀번호·토큰은 절대 두지 않는다. 끄면 지운다.
// - 로그인 유지: 끄면 브라우저 세션 쿠키(창을 닫으면 끊김), 켜면 쿠키 7일 + 쓰는 동안 7일씩 이어진다.

import type { ChangeEvent } from "react";

/** 「로그인 유지」를 켰을 때 마지막 사용 뒤 세션이 사는 날수. 서버 상수와 같은 값이다. */
export const KEEP_LOGIN_DAYS = 7;

/** 아이디를 두는 localStorage 키의 기본값. localStorage 는 출처(호스트+포트)마다 따로라 서비스끼리 안 겹친다. */
export const SAVED_LOGIN_ID_KEY = "mops.login.savedId";

export interface LoginRememberProps {
  saveId: boolean;
  onSaveIdChange: (on: boolean) => void;
  keep: boolean;
  onKeepChange: (on: boolean) => void;
  disabled?: boolean;
  // 사전(i18n)이 있는 서비스가 같은 값을 넘기는 자리. 안 주면 기본 문구.
  saveIdLabel?: string;
  keepLabel?: string;
  className?: string;
}

/** 로그인 단추 바로 위에 두는 체크 둘. 폼 안에 넣는다(라벨 전체가 누르는 자리 — v3 .b-check). */
export function LoginRemember({
  saveId, onSaveIdChange, keep, onKeepChange, disabled,
  saveIdLabel = "아이디 저장",
  keepLabel = `로그인 유지(${KEEP_LOGIN_DAYS}일)`,
  className,
}: LoginRememberProps) {
  return (
    <div className={className ? `mtotp-remember ${className}` : "mtotp-remember"}>
      <label className="b-check">
        <input
          type="checkbox"
          name="saveId"
          checked={saveId}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onSaveIdChange(e.target.checked)}
        />
        {saveIdLabel}
      </label>
      <label className="b-check">
        <input
          type="checkbox"
          name="remember"
          checked={keep}
          disabled={disabled}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onKeepChange(e.target.checked)}
        />
        {keepLabel}
      </label>
    </div>
  );
}

// localStorage 는 사생활 보호 창·막힌 사이트 데이터에서 읽기·쓰기가 던질 수 있다. 그때는 저장이
// 안 될 뿐 로그인은 돼야 하므로 조용히 넘긴다(저장은 편의다 — 로그인을 막을 이유가 아니다).
function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** 저장해 둔 아이디. 없거나 읽을 수 없으면 null — 「아이디 저장」 체크의 처음 값은 이것이 있느냐로 정한다. */
export function readSavedLoginId(key: string = SAVED_LOGIN_ID_KEY): string | null {
  try {
    const v = storage()?.getItem(key);
    return v && v.trim() ? v : null;
  } catch {
    return null;
  }
}

/** 아이디를 저장하거나(문자열) 지운다(null·빈 값). 비밀번호 단계가 받아들여진 뒤 부른다 —
 *  틀린 아이디를 저장해 다음 방문에 다시 틀리게 두지 않으려는 것이다. */
export function writeSavedLoginId(id: string | null, key: string = SAVED_LOGIN_ID_KEY): void {
  try {
    const s = storage();
    if (!s) return;
    const v = id?.trim();
    if (v) s.setItem(key, v);
    else s.removeItem(key);
  } catch {
    // 위 storage() 주석과 같은 이유로 넘긴다.
  }
}
